import { HexGridType } from 'js/bubbleCore/types';
import { isPointyTop } from 'js/bubbleCore/utils/isPointyTop';
import { Vector3 } from 'framework/utils/Vector3';
import { Vector2 } from 'framework/utils/Vector2';

export type HexHash = string;

type TempHexes = {[key in HexGridType]: Hex};

export class Hex {
  public static getTempHexes(): TempHexes {
    return {
      [HexGridType.OddR]: new Hex(HexGridType.OddR),
      [HexGridType.OddQ]: new Hex(HexGridType.OddQ),
      [HexGridType.EvenQ]: new Hex(HexGridType.EvenQ),
      [HexGridType.EvenR]: new Hex(HexGridType.EvenR),
    };
  }

  public readonly cubePosition: Vector3 = new Vector3();
  public readonly offsetPosition: Vector2 = new Vector2();

  private readonly type: HexGridType;

  constructor(type: HexGridType) {
    this.type = type;

    this.cubePosition.onChange = (vector3) => this.updateOffsetPosition();
    this.offsetPosition.onChange = (vector2) => this.updateCubePosition();
  }

  public static axialToCube(q: number, r: number): Vector3 {
    return new Vector3(q, -q - r, r);
  }

  public clone(): Hex {
    return new Hex(this.type).copyFrom(this);
  }

  public setAxial(q: number, r: number): this {
    this.cubePosition.set(q, -q - r, r);
    return this;
  }

  public setOffset(r: number, q: number): this {
    this.offsetPosition.set(r, q);
    return this;
  }

  public setCube(x: number, y: number, z: number): this {
    this.cubePosition.set(x, y, z);
    return this;
  }

  public scale(factor: number): this {
    const {x, y, z} = this.cubePosition;
    this.setCube(x * factor, y * factor, z * factor);
    return this;
  }

  public getPosition(out?: PIXI.Point): PIXI.Point {
    const result = out || new PIXI.Point;

    const {x: q, z: r} = this.cubePosition;

    if (isPointyTop(this.type)) {
      result.set(
        Math.sqrt(3) * (q + r / 2),
        (3 / 2) * r,
      );
    } else {
      result.set(
        (3 / 2) * q,
        Math.sqrt(3) * (r + q / 2),
      );
    }

    return result;
  }

  public setFromPosition(x: number, y: number): this {
    let q: number;
    let r: number;

    if (isPointyTop(this.type)) {
      q = (x * Math.sqrt(3)/3 - y / 3);
      r = y * 2/3;
    } else {
      q = x * 2/3;
      r = (-x / 3 + Math.sqrt(3)/3 * y);
    }

    this.setAxial(q, r);

    return this;
  }

  public getCorners(): PIXI.Point[] {
    const result: PIXI.Point[] = [];
    const position = this.getPosition();
    const pointyTop = isPointyTop(this.type);
    for (let i = 0; i < 6; i++) {
      const angleDeg = 60 * i + (pointyTop ? 30 : 0);
      const angleRad = Math.PI / 180 * angleDeg;
      result.push(new PIXI.Point(
        position.x + Math.cos(angleRad),
        position.y + Math.sin(angleRad),
      ));
    }
    return result;
  }

  public add(otherHex: Hex): this {
    const {x, y, z} = this.cubePosition;
    const {x: x2, y: y2, z: z2} = otherHex.cubePosition;
    this.cubePosition.set(x + x2, y + y2, z + z2);
    return this;
  }

  public subtract(otherHex: Hex): this {
    const {x, y, z} = this.cubePosition;
    const {x: x2, y: y2, z: z2} = otherHex.cubePosition;
    this.cubePosition.set(x - x2, y - y2, z - z2);
    return this;
  }

  public equalTo(otherHex: Hex): boolean {
    const {x, y, z} = this.cubePosition;
    const {x: x2, y: y2, z: z2} = otherHex.cubePosition;
    return x === x2 && y === y2 && z === z2;
  }

  public isNeighbourOf(otherHex: Hex): boolean {
    return this.distanceTo(otherHex) === 1;
  }

  public distanceTo(otherHex: Hex): number {
    const {x, y, z} = this.cubePosition;
    const {x: x2, y: y2, z: z2} = otherHex.cubePosition;
    return Math.max(Math.abs(x - x2), Math.abs(y - y2), Math.abs(z - z2));
  }

  public copyTo(otherHex): void {
    const {x, y, z} = this.cubePosition;
    otherHex.cubePosition.set(x, y, z);
  }

  public copyFrom(otherHex): this {
    const {x, y, z} = otherHex.cubePosition;
    this.cubePosition.set(x, y, z);
    return this;
  }

  public round(): this {
    const {x, y, z} = this.cubePosition;

    let rx = Math.round(x);
    let ry = Math.round(y);
    let rz = Math.round(z);

    const x_diff = Math.abs(rx - x);
    const y_diff = Math.abs(ry - y);
    const z_diff = Math.abs(rz - z);

    if (x_diff > y_diff && x_diff > z_diff) {
      rx = -ry - rz;
    }else if (y_diff > z_diff) {
      ry = -rx - rz;
    } else {
      rz = -rx - ry;
    }

    this.cubePosition.set(rx, ry, rz);

    return this;
  }

  public getHash(): HexHash {
    return this.cubePosition.toString();
  }

  public fromHash(hash: HexHash): this {
    const pos = hash.split('x').map(Number);
    this.cubePosition.set(pos[0], pos[1], pos[2]);
    return this;
  }

  private updateCubePosition() {
    switch (this.type) {
      case (HexGridType.OddR): this.oddRToCube(); break;
      case (HexGridType.EvenR): this.evenRToCube(); break;
      case (HexGridType.OddQ): this.oddQToCube(); break;
      case (HexGridType.EvenQ): this.evenQToCube(); break;
    }
  }

  private updateOffsetPosition() {
    switch (this.type) {
      case (HexGridType.OddR): this.cubeToOddR(); break;
      case (HexGridType.EvenR): this.cubeToEvenR(); break;
      case (HexGridType.OddQ): this.cubeToOddQ(); break;
      case (HexGridType.EvenQ): this.cubeToEvenQ(); break;
    }
  }

  private cubeToEvenQ() {
    //   this.col = this.x;
    //   this.row = this.z+(this.x + Math.abs(this.x%2))/2;
    const {x, y, z} = this.cubePosition;
    this.offsetPosition.setSilent(x, z + (x + Math.abs(x % 2)) / 2);
  }

  private evenQToCube() {
    //   this.x = this.col;
    //   this.z = this.row-(this.col+Math.abs(this.col%2))/2;
    // this.y = -this.x-this.z;
    const {x: col, y: row} = this.offsetPosition;
    const z = row - (col + Math.abs(col % 2)) / 2;
    this.cubePosition.setSilent(col, -col - z, z);
  }

  private cubeToOddR() {
    //   this.col = this.x+(this.z-Math.abs(this.z%2))/2;
    //   this.row = this.z;
    const {x, y, z} = this.cubePosition;
    this.offsetPosition.setSilent(x + (z - Math.abs(z % 2))/2, z);
  }

  private oddRToCube() {
    //   this.x = this.col-(this.row-Math.abs(this.row%2))/2;
    //   this.z = this.row;
    //   this.y = -this.x-this.z;
    const {x: col, y: row} = this.offsetPosition;
    const x = col - (row - Math.abs(row % 2)) / 2;
    this.cubePosition.setSilent(x, -x - row, row);
  }

  private cubeToOddQ() {
    // this.col = this.x;
    // this.row = this.z + (this.x-Math.abs(this.x%2))/2;
    const {x, y, z} = this.cubePosition;
    this.offsetPosition.setSilent(x, z + (x - Math.abs(x % 2)) / 2);
  }

  private oddQToCube() {
    // this.x = this.col;
    // this.z = this.row-(this.col-Math.abs(this.col%2))/2;
    // this.y = -this.x-this.z;
    const {x: col, y: row} = this.offsetPosition;
    const z = row-(col-Math.abs(col%2))/2;
    this.cubePosition.setSilent(col, -col - z, z);
  }

  private cubeToEvenR() {
    // this.col = this.x+(this.z+Math.abs(this.z%2))/2;
    // this.row = this.z;
    const {x, y, z} = this.cubePosition;
    this.offsetPosition.setSilent(x + (z + Math.abs(z % 2)) / 2, z);
  }

  private evenRToCube() {
    // this.x = this.col-(this.row+Math.abs(this.row%2))/2;
    // this.z = this.row;
    // this.y = -this.x-this.z;
    const {x: col, y: row} = this.offsetPosition;
    const x = col - (row + Math.abs(row % 2)) / 2;
    this.cubePosition.set(x, -x - row, row);
  }

  public debugOffsetStr() {
    return `${this.offsetPosition.x}x${this.offsetPosition.y}`;
  }
}
